import React, { useCallback, useRef } from 'react';
import { useIntl } from 'react-intl';
import { Col, Row } from 'reactstrap';
import { faEnvelope } from '@fortawesome/pro-light-svg-icons/faEnvelope';
import { faUser } from '@fortawesome/pro-light-svg-icons/faUser';
import type { MailboxProviderId } from 'fym-shared/src/music24/outreach/mailboxProviders';
import { TypedMemo } from '../../../common/types';
import { DetectionBanner } from './DetectionBanner';
import { GuideHead } from './GuideHead';
import { GuideInput } from './GuideInput';
import { ProviderGrid } from './ProviderGrid';
import { TrustStrip } from './TrustStrip';
import { UnsupportedCard } from './UnsupportedCard';
import { providerOf } from './mailboxGuideState';
import { mailboxGuideMessages as messages } from './mailboxGuideMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the guide; each part uses its own slice of it */
import styles from './MailboxGuide.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface StepWhichEmailProps {
  email: string;
  displayName: string;
  onEmail: (email: string) => void;
  onDisplayName: (name: string) => void;
  /** What the lookup found (null after "Not Gmail?"). */
  detectedId: MailboxProviderId | null;
  isDetecting: boolean;
  /** The provider of the guide: the picked tile, else the detected one. */
  providerId: MailboxProviderId | null;
  onPick: (id: MailboxProviderId) => void;
  onDismissDetection: () => void;
  /** A way forward of the "not supported" card: clear the address and pick that tile. */
  onStartOver: (id: MailboxProviderId) => void;
}

/**
 * Step 1 of 3 (design outreach-v2 p1, p2, p4): the two fields side by side, then the detection
 * banner, the tiles and the three promises, or the "not supported" card. Frame p3 is not built.
 */
const StepWhichEmailComponent: React.FC<StepWhichEmailProps> = ({
  email,
  displayName,
  onEmail,
  onDisplayName,
  detectedId,
  isDetecting,
  providerId,
  onPick,
  onDismissDetection,
  onStartOver,
}) => {
  const intl = useIntl();
  const emailRef = useRef<HTMLInputElement>(null);
  const unsupported = providerId !== null && providerOf(providerId).passwordKind === 'unsupported';
  const showBanner = !unsupported && providerId !== null && providerId === detectedId && detectedId !== 'other';
  const startOver = useCallback(
    (id: MailboxProviderId) => {
      onStartOver(id);
      emailRef.current?.focus();
    },
    [onStartOver]
  );

  return (
    <>
      <GuideHead
        step={1}
        title={intl.formatMessage(messages.whichTitle)}
        intro={intl.formatMessage(messages.whichIntro)}
      />
      <div className={styles.body}>
        <Row className="g-3">
          <Col xs={12} sm={7}>
            <GuideInput
              id="mailbox-guide-email"
              label={intl.formatMessage(messages.emailLabel)}
              icon={faEnvelope}
              inputRef={emailRef}
              type="email"
              autoComplete="email"
              placeholder={intl.formatMessage(messages.emailPlaceholder)}
              value={email}
              onChange={onEmail}
            />
          </Col>
          <Col xs={12} sm={5}>
            <GuideInput
              id="mailbox-guide-name"
              label={intl.formatMessage(messages.nameLabel)}
              icon={faUser}
              maxLength={120}
              value={displayName}
              onChange={onDisplayName}
            />
          </Col>
        </Row>
        {isDetecting && <p className="small text-body-secondary mb-0">{intl.formatMessage(messages.detecting)}</p>}
        {showBanner && providerId !== null && (
          <DetectionBanner providerId={providerId} email={email} onDismiss={onDismissDetection} />
        )}
        {unsupported && providerId !== null ? (
          <UnsupportedCard providerId={providerId} onPickWay={startOver} />
        ) : (
          <>
            <div className={styles.divider}>{intl.formatMessage(messages.orPick)}</div>
            <ProviderGrid value={providerId} onPick={onPick} />
            {providerId === null && <TrustStrip />}
          </>
        )}
      </div>
    </>
  );
};

export const StepWhichEmail = TypedMemo(StepWhichEmailComponent);
